import { Cloudinary } from "@cloudinary/url-gen"
import { Resize } from "@cloudinary/url-gen/actions/resize"
import { Box, DialogContent, Modal } from "@mui/material"
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Outlet, useParams } from "react-router"
import { theme, TI } from "src/utilities/theme"

import { DGText } from "../../components/common/DGText"
import { Footer } from "../../components/common/Footer"
import { GenericButton } from "../../components/common/GenericButton"
import { GradientOverlay } from "../../components/common/GradientOverlay/GradientOverlay"
import { Image } from "../../components/common/Image"
import empty from "../../components/images/empty.png"
import { ProgressSpinner } from "../../components/ProgressSpinner"
import {
  Slider,
  Sliders,
  TeaserDimension,
  TeaserDimensions,
  TTeaser,
} from "../../components/Sliders/Slider"
import { curatedContentLinkBg, Teaser } from "../../components/Sliders/Teaser"
import {
  CuratedContentLink,
  CuratedContentPageQuery,
  FacetType,
  PictureType,
  SliderLayout,
} from "../../generated/graphql"
import useSdk, { ErrorLevel } from "../../hooks/use-sdk"
import { useSliders } from "../../hooks/use-sliders"
import { useSmoothNavigate } from "../../hooks/use-smooth-navigate"
import { useTransitions } from "../../hooks/use-transitions"
import useTranslate from "../../hooks/use-translate"
import { appStore } from "../../store/AppStore"
import {
  ccFactor,
  CloudinaryConf,
  comb,
  CuratedContentSlugV2,
  s,
} from "../../utilities/common"
import { dimensions, maxSliderSize } from "../../utilities/constants"
import {
  AddFocusFunction,
  GetFocusFunction,
  useFocusHelper,
} from "../../utilities/use-focus-helper"
import { FacetBrowse } from "../Search/Modals/FacetBrowse/FacetBrowse"

import { CategoryScreenFocusKeys } from "./category-focus-keys"

const cld = new Cloudinary(CloudinaryConf)
const colSpacing =
  TeaserDimensions[TeaserDimension.LandscapeSmallTeaserNoText].colSpacing

const useCategoryScreen = () => {
  const { slug } = useParams()
  const sdk = useSdk(ErrorLevel.WithPrompt)
  const [data, setData] = useState<CuratedContentPageQuery>()
  const load = useCallback(async () => {
    if (!slug) return undefined
    const result = await sdk.curatedContentPage({
      slug,
      sliderPageSize:
        slug === CuratedContentSlugV2.Live ||
        slug === "genres" ||
        slug === "epochs"
          ? 999
          : maxSliderSize,
    })
    setData(result)
  }, [slug])

  const sliders = useSliders(data?.curatedContentPage.curatedContent)

  useEffect(() => {
    load()
  }, [])

  return { title: data?.curatedContentPage.title, sliders, data }
}

const Screen = ({
  title,
  slug,
  sliders,
  data,
}: {
  title: string
  slug: string
  sliders: Sliders
  data: CuratedContentPageQuery
}) => {
  const [facetsOpen, setFacetsOpen] = useState(false)
  const t = useTranslate()

  const facetType = useMemo(() => {
    if (!slug) return
    switch (slug) {
      case "conductors":
        return FacetType.Conductors
      case "composers":
        return FacetType.Composers
      case "ensembles":
        return FacetType.Ensembles
      case "soloists":
        return FacetType.Soloists
      case "venues":
        return FacetType.Venue
      default:
        return undefined
    }
  }, [slug])

  const { addFocus, getFocus, getMetaInfo } = useFocusHelper()

  const { ref, focusKey } = useFocusable({ isFocusBoundary: true })
  const transition = useTransitions()

  const bgImage = useMemo(() => {
    const pictures = (
      data.curatedContentPage.navigation?.items.find(
        (item) =>
          item.__typename === "CuratedContentLink" && item.slugV2 === slug,
      ) as CuratedContentLink
    ).pictures
    const imageUrl = pictures.find((p) => p.type === PictureType.Teaser)?.url

    return cld
      .image(imageUrl)
      .resize(Resize.scale().width(ccFactor(dimensions.width)))
      .format("jpg")
      .setDeliveryType("fetch")
      .toURL()
  }, [data])

  useEffect(() => {
    const meta = getMetaInfo<{ glyph: string }>()
    if (meta && "glyph" in meta && meta.glyph) setFacetsOpen(true)
  }, [])

  const scrollRef = useRef<HTMLElement>(null)
  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        style={{ transition, height: "100%", width: "100%" }}
        className={"loading_in"}
        ref={scrollRef}
      >
        <GradientOverlay type={"full"} />
        <Box
          sx={{
            position: "absolute",
            width: "100vw",
            height: "100vh",
            overflow: "hidden",
            isolation: "isolate",
          }}
        >
          <Box
            sx={{
              background:
                curatedContentLinkBg[slug as keyof typeof curatedContentLinkBg],
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              zIndex: 0,
            }}
          />
          <Image
            alt={""}
            src={bgImage}
            sx={{
              position: "relative",
              width: "100vw",
              height: "100vh",
              zIndex: 1,
              top: 0,
              backgroundRepeat: "no-repeat",
              backgroundImage: `url(${empty})`,
              backgroundPosition: "center",
              backgroundSize: s("360px"),
              backgroundColor: "transparent",
              mixBlendMode: "multiply",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              zIndex: 2,
              height: s("470px"),
              width: "100vw",
              background:
                "linear-gradient(180deg, rgba(21, 31, 49, 0) 0%, #151F31 100%)",
            }}
          ></Box>
        </Box>
        <Box
          ref={ref}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            minHeight: "100%",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                flex: 1,
                pt: s("60px"),
                pl: s("96px"),
                pr: s("85px"),
                mb: s("64px"),
              }}
            >
              <DGText
                sx={{
                  color: theme.palette.white.main,
                  zIndex: 1,
                }}
                tx={TI.H04tv}
              >
                {title}
              </DGText>
              {facetType && (
                <GenericButton
                  ariaLabel={t("slider__show_more")}
                  focusKey={
                    CategoryScreenFocusKeys.FacetButton + ":" + facetType
                  }
                  sx={{ ml: s("50px") }}
                  onEnterPress={() => setFacetsOpen(true)}
                >
                  <DGText
                    tx={TI.Subheadline07tv}
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {t("slider__show_more")} {title.toLowerCase()}
                  </DGText>
                </GenericButton>
              )}
            </Box>
            <FocusedArea
              scrollRef={scrollRef}
              sliders={sliders}
              slug={slug}
              addFocus={addFocus}
              getFocus={getFocus}
            ></FocusedArea>
          </Box>
          {sliders ? <Footer /> : null}
          <FacetBrowse
            getMetaInfo={getMetaInfo}
            facetType={facetType}
            open={comb.ine(facetsOpen, setFacetsOpen)}
          />
        </Box>
      </Box>
    </FocusContext.Provider>
  )
}

const FocusedArea = ({
  slug,
  sliders,
  scrollRef,
  addFocus,
  getFocus,
}: {
  slug: string
  sliders: Sliders
  scrollRef: RefObject<HTMLElement | null>
  addFocus: AddFocusFunction
  getFocus: GetFocusFunction
}) => {
  const navigate = useSmoothNavigate()
  const setSpinner = appStore((state) => state.setSpinner)

  const { ref, focusSelf, focusKey } = useFocusable({
    isFocusBoundary: true,
    focusBoundaryDirections: ["left", "right", "down"],
  })

  useEffect(() => {
    const currentKey = getFocus()
    if (sliders)
      setTimeout(() => {
        setSpinner("out")
        if (!currentKey) focusSelf()
      }, 200)
  }, [focusSelf])

  return (
    <FocusContext.Provider value={focusKey}>
      <Box ref={ref}>
        {slug !== "epochs" && slug !== "genres" && sliders ? (
          sliders.map((slider, sliderIndex) => (
            <Slider
              key={slug + slider.title + slider.id}
              onPressBrowse={() => {
                addFocus()
                navigate(`../browse/0/${slider.id}/1/0`)
                scrollRef.current?.classList.add("loading_out")
              }}
              onPressTitle={(d?: TTeaser, teaserIndex?: number) => {
                if (!d || teaserIndex === undefined) return
                addFocus()
                navigate(
                  `../detail/${d.type}/${d.id}/${sliderIndex + 1}/${
                    teaserIndex + 1
                  }/${slider.id}`,
                )
                scrollRef.current?.classList.add("loading_out")
              }}
              onFocus={({ top: y, height }, _, query) => {
                if (!(query && query.noScroll) && scrollRef.current) {
                  if (sliderIndex === 0) {
                    scrollRef.current.style.transform = "translate3d(0, 0, 0)"
                    return
                  }
                  scrollRef.current.style.transform =
                    "translate3d(0, " +
                    Math.min(0, -y + (dimensions.height - height) / 2) +
                    "px, 0)"
                }
              }}
              title={slider.title}
              prefix={slug}
              data={slider}
              dimensions={slider.style}
              margin={sliderIndex < sliders?.length - 1}
            />
          ))
        ) : (
          <Box
            component={"div"}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              mt: "-40px",
              ml: `-${colSpacing}`,
              pl: s("96px"),
              pr: s("96px"),
              width: `calc(100% + ${colSpacing})`,
              boxSizing: "border-box",
            }}
          >
            {sliders
              .flatMap((slider) => slider.teasers)
              .map((item, i) => {
                if (item)
                  return (
                    <Box
                      sx={{ pl: colSpacing, pt: "40px" }}
                      key={"browse-" + item.id + i}
                    >
                      <Teaser
                        onPress={(d?: TTeaser, teaserIndex?: number) => {
                          if (!d || teaserIndex === undefined) return
                          addFocus()
                          navigate(
                            `../detail/${d.type}/${d.id}/0/${teaserIndex + 1}/0`, // sliders are merged so we cannot track them appropriately
                          )
                          scrollRef.current?.classList.add("loading_out")
                        }}
                        onFocusOverride={({ top: y, height }, _, query) => {
                          if (!(query && query.noScroll) && scrollRef.current) {
                            scrollRef.current.style.transform =
                              "translate3d(0, " +
                              Math.max(
                                -(
                                  scrollRef.current.scrollHeight -
                                  dimensions.height
                                ),
                                Math.min(
                                  0,
                                  -y + (dimensions.height - height) / 2,
                                ),
                              ) +
                              "px, 0)"
                          }
                        }}
                        dimensions={TeaserDimension.LandscapeSmallTeaserNoText}
                        data={item}
                        scrollDirection={"vertical"}
                        me={"categoryScreen" + slug}
                        scrollView={ref}
                        index={i}
                        colMargin={false}
                        sliderLayout={SliderLayout.Regular}
                      />
                    </Box>
                  )
              })}
          </Box>
        )}
      </Box>
    </FocusContext.Provider>
  )
}

export const CategoryScreen = () => {
  const { slug } = useParams()

  const ref = useRef<HTMLElement>(null)
  const { sliders, title, data } = useCategoryScreen()

  if (!title || !sliders || !slug || !data) return <ProgressSpinner />

  return (
    <>
      <Modal
        key={"categoryscreen" + slug}
        open={true}
        sx={{
          height: "100vh",
          width: "100vw",
          backgroundColor: theme.palette.primary.main,
        }}
        hideBackdrop
      >
        <DialogContent
          sx={{
            backgroundColor: theme.palette.primary.main,
            overflowY: "visible",
            display: "flex",
            height: "100%",
          }}
          ref={ref}
        >
          <Screen title={title} slug={slug} sliders={sliders} data={data} />
        </DialogContent>
      </Modal>
      <Outlet />
    </>
  )
}
